import Button from '../button';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/arrow-left.svg';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down.svg';
import './styles.scss';
import { useRef, useState, useEffect, useMemo, useCallback, memo } from 'react';

// Array de questões movido para fora do componente para garantir referência estável
const QUESTIONS_DATA = [
    {
        title: "Qual é a melhor plataforma para vender produtos fitness?",
        answer: "Se você vende qualquer produto, ou serviço na área da saúde, já deve ter percebido que as plataformas atuais são genéricas demais... E foi justamente para resolver isso que o Ginastee foi lapidado desde o início: permitir com que profissionais da saúde tenham diferencial, escalando suas vendas. Se o fitness é o seu nicho, o Ginastee é o lugar pra você!"
    },
    {
        title: "O que é o Ginastee e como funciona?",
        answer: "Você está diante da primeira plataforma no nicho fitness projetada para você crescer sua receita no digital. O Ginastee é o lugar onde você cria seus conteúdos privados, realiza desafios, e vende seus materiais."
    },
    {
        title: "Quanto o Ginastee cobra por venda?",
        answer: "Para ter seu produto na plataforma, você não paga nenhuma mensalidade. O Ginastee cobra apenas um pequeno percentual de sucesso quando você vende um produto. Este percentual é de 9,49% + R$1,99 por cobrança recebida."
    },
    {
        title: "Em quanto tempo recebo as cobranças na minha conta?",
        answer: "Os pagamentos via PIX são recebidos em D+2. Já os pagamentos no crédito, você pode escolher receber em D+10, com todas as parcelas antecipadas, ou em D+32, mês a mês para cada parcela."
    },
    {
        title: "Quais são os meios de pagamento aceitos pelo Ginastee?",
        answer: "Cartão de crédito à vista ou parcelado em até 12x, e PIX."
    },
    {
        title: "Como metrifico a minha performance de vendas?",
        answer: "O Ginastee conta com um painel robusto, que te dá todas as informações para você metrificar o sucesso de suas vendas."
    },
    {
        title: "Existe algum custo adicional para vender no Ginastee?",
        answer: "Não, o Ginastee apenas cobra um pequeno percentual de sucesso quando você vende um produto."
    },
];

// Componente BlockQuestion memoizado para evitar re-renders desnecessários
const BlockQuestion = memo(function BlockQuestion({ question, index }) {
    const REF_answer = useRef()
    const [isOpen, setIsOpen] = useState(false)
    const [typedText, setTypedText] = useState('')
    const [isTypingComplete, setIsTypingComplete] = useState(false)
    const typingIntervalRef = useRef(null)
    const currentIndexRef = useRef(0)
    const hasStartedTypingRef = useRef(false)
    const questionAnswerRef = useRef(question.answer)

    // Atualizar ref quando question.answer mudar (mas sem causar re-render)
    useEffect(() => {
        questionAnswerRef.current = question.answer
    }, [question.answer])

    // Sincronizar classe CSS com o estado
    useEffect(() => {
        if (REF_answer.current) {
            if (isOpen) {
                REF_answer.current.classList.add('open')
            } else {
                REF_answer.current.classList.remove('open')
            }
        }
    }, [isOpen])

    // Limpar intervalo quando componente desmontar
    useEffect(() => {
        return () => {
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current)
                typingIntervalRef.current = null
            }
        }
    }, [])

    // Limpar estado quando fechar
    useEffect(() => {
        if (!isOpen) {
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current)
                typingIntervalRef.current = null
            }
            currentIndexRef.current = 0
            hasStartedTypingRef.current = false
            setIsTypingComplete(false)
            setTypedText('')
        }
    }, [isOpen])

    // Iniciar typing quando abrir
    useEffect(() => {
        // Só iniciar typing se estiver aberto, houver resposta, e ainda não tiver começado
        if (isOpen && questionAnswerRef.current && !hasStartedTypingRef.current) {
            const fullText = questionAnswerRef.current
            currentIndexRef.current = 0
            hasStartedTypingRef.current = true
            setIsTypingComplete(false)
            setTypedText('')

            // Limpar intervalo anterior se existir (defesa)
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current)
                typingIntervalRef.current = null
            }

            typingIntervalRef.current = setInterval(() => {
                if (currentIndexRef.current < fullText.length) {
                    setTypedText(fullText.substring(0, currentIndexRef.current + 1))
                    currentIndexRef.current++
                } else {
                    // Typing completo - garantir que o texto completo esteja no estado
                    setTypedText(fullText)
                    setIsTypingComplete(true)
                    if (typingIntervalRef.current) {
                        clearInterval(typingIntervalRef.current)
                        typingIntervalRef.current = null
                    }
                }
            }, 20) // Velocidade: 30ms por caractere

            // Cleanup function - limpar intervalo se necessário
            return () => {
                // Só limpar se o componente estiver sendo desmontado ou fechado
                // O estado será preservado mesmo após re-renders
            }
        }
    }, [isOpen]) // Apenas isOpen como dependência para evitar re-iniciar o typing

    const handleToggleAnswer = useCallback(() => {
        setIsOpen(prev => !prev)
    }, [])

    // Determinar o texto a exibir
    // Se estiver aberto: mostrar texto digitado durante typing, ou texto completo após typing
    // Se estiver fechado: não mostrar nada
    const answerText = questionAnswerRef.current || ''
    let displayText = ''
    let showCursor = false

    if (isOpen && answerText) {
        if (isTypingComplete || typedText.length >= answerText.length) {
            // Typing completo - sempre mostrar o texto completo da questão
            displayText = answerText
            showCursor = false
        } else if (typedText.length > 0) {
            // Durante o typing, mostrar o texto parcial
            displayText = typedText
            showCursor = true
        } else {
            // Ainda não começou a digitar
            displayText = ''
            showCursor = false
        }
    } else if (isOpen && !answerText) {
        // Aberto mas sem resposta
        displayText = ''
        showCursor = false
    } else {
        // Fechado
        displayText = ''
        showCursor = false
    }

    return (
        <div className='question'>
            <div className='title-block' onClick={handleToggleAnswer}>
                <h3 className='question-badge'>#{(index < 9 ? '0' : '') + (index + 1)}</h3>
                <h4 className='title'>{question.title}</h4>
                <ArrowDownIcon />
            </div>
            <div className='answer' ref={REF_answer}>
                {displayText}
                {showCursor && (
                    <span className='typing-cursor'>|</span>
                )}
            </div>
        </div>
    )
}, (prevProps, nextProps) => {
    // Comparação personalizada para React.memo
    // Só re-renderiza se question.title ou question.answer mudarem
    return (
        prevProps.question.title === nextProps.question.title &&
        prevProps.question.answer === nextProps.question.answer &&
        prevProps.index === nextProps.index
    )
})

// Função auxiliar para comparar objetos de estilo
function areStylesEqual(style1, style2) {
    if (!style1 && !style2) return true
    if (!style1 || !style2) return false
    const keys1 = Object.keys(style1)
    const keys2 = Object.keys(style2)
    if (keys1.length !== keys2.length) return false
    for (let key of keys1) {
        if (style1[key] !== style2[key]) return false
    }
    return true
}

// Componente FAQ memoizado para evitar re-renders causados por scroll
const FAQ = memo(function FAQ({ style, className, questions = QUESTIONS_DATA, ...otherProps }) {
    // Usar useMemo para garantir referência estável (mesmo que já esteja fora do componente)



    return (
        <div className={`faq ${className || ''}`} style={style} {...otherProps}>
            <div className='container'>
                <h1>Alguma dúvida? Temos a resposta!</h1>
                <h4 style={{ width: '80%', opacity: 0.8 }} className='mb-2'>E caso não encontre ela aqui, você pode sempre contar com a ajuda da nossa equipe</h4>
                <Button.Large
                    className='mb-4'
                    text="Entrar em contato"
                    rightIcon={<ArrowLeftIcon />}
                    onClick={window.config.redirects.openSupport}
                />

                <div className='questions-block'>
                    {
                        questions.map((question, index) => (
                            <>
                                {index !== 0 && <hr className='separator' />}
                                <BlockQuestion index={index} question={question} />
                            </>
                        ))
                    }
                </div>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    // Comparação personalizada: só re-renderiza se className ou style mudarem significativamente
    // Ignora outras props que podem mudar mas não afetam o componente
    if (prevProps.className !== nextProps.className) return false
    if (!areStylesEqual(prevProps.style, nextProps.style)) return false
    return true
})

export default FAQ